import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis'
import scaleAnimationPositions from '../../utils/scaleAnimationKeyFrames'
import useGateBoundingDataChecker from '../../utils/gateBoundingDataChecker'

// ✅ Helper do skalowania geometrii — poza komponentem
function scaleGateGeometry(sourceGeometry, gateHeight, gateWidth) {
  return cloneAndScaleUV(
    sourceGeometry,
    1,
    gateHeight / 100,
    gateWidth / 100,
    gateWidth / 100,
    gateHeight / 100
  )
}

const DrzwiFramuga = forwardRef(({ gate, onUpdate, textures, ...props }, ref) => {
  const group = useRef()
  const { nodes, materials, animations } = useGLTF('/models/gates/Brama-dwuskrzydlowa-2.glb')

  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const mixerRef = useRef(null)

  useImperativeHandle(ref, () => group.current)

  // ==========================================================
  // ✅ FIX #1: stabilne "zamknięcie" animacji na klatce 0
  // (żeby bbox nie łapał pół-klatki otwarcia + żeby nie było driftu)
  // ==========================================================
  const freezeClosedPose = useCallback(() => {
    if (!mixerRef.current) return
    if (!group.current) return

    // ustaw wszystkie akcje na time=0 i zamroź
    const mixer = mixerRef.current
    mixer.stopAllAction()

    // ważne: clipAction musi być "played" aby zastosować stan
    // ale natychmiast go pauzujemy i timeScale=0
    ;(mixer._actions || []).forEach((action) => {
      action.reset()
      action.enabled = true
      action.clampWhenFinished = true
      action.setLoop(THREE.LoopOnce, 1)
      action.time = 0
      action.timeScale = 0
      action.paused = false
      action.play()
      action.paused = true
    })

    // wymuś update 0, żeby stan time=0 siadł na kościach/obiektach
    mixer.update(0)

    isOpenRef.current = false
    isAnimatingRef.current = false
    setIsOpen(false)
  }, [])

  // ===== Animations =====
  const scaledAnimations = useMemo(() => {
    const scale = new THREE.Vector3(1, gate.height / 100, gate.width / 100)
    return (animations || []).map((clip) => scaleAnimationPositions(clip, scale))
  }, [animations, gate.height, gate.width])

  useEffect(() => {
    if (!group.current) return
    if (!scaledAnimations || scaledAnimations.length === 0) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(group.current)
    }

    mixerRef.current = new THREE.AnimationMixer(group.current)

    scaledAnimations.forEach((clip) => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
      action.timeScale = 0
      action.time = 0
      action.play()
      action.paused = true
    })

    // ✅ FIX: po stworzeniu akcji wymuś "closed pose"
    freezeClosedPose()
  }, [scaledAnimations, freezeClosedPose])

  // ✅ Animation loop — stabilny (zostawiam jak masz)
  useEffect(() => {
    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  // ==========================================================
  // ✅ FIX #2: resetuj po zmianie WYMiarów i pozycji (liczby, nie obiekt)
  // (gate.position jako obiekt bywa nową referencją -> spam resetów)
  // ==========================================================
  useEffect(() => {
    if (!mixerRef.current) return
    if (!scaledAnimations || scaledAnimations.length === 0) return

    freezeClosedPose()
  }, [
    // pozycja jako liczby
    gate.position?.x,
    gate.position?.y,
    gate.position?.z,
    // oraz wymiary
    gate.width,
    gate.height,
    // i klipy
    scaledAnimations,
    freezeClosedPose,
  ])

  // ==========================================================
  // ✅ FIX #3: bbox/wycięcie ma się aktualizować gdy zmieniasz width/height/pos/length
  // ale NIE podczas animacji. I nie ma łapać pół-otwarcia.
  // ==========================================================
  const lastCutSigRef = useRef('')

  const handleBoundingDataChanged = useCallback(() => {
    if (isAnimatingRef.current) return
    if (!group.current) return
    if (typeof onUpdate !== 'function') return

    // sygnatura kiedy trzeba odświeżyć wycięcie
    // (nie ruszam Twojej logiki wycinania, tylko decyduję kiedy wolno przeliczyć)
    const sig = [
      props.length,
      gate.width,
      gate.height,
      gate.position?.x,
      gate.position?.y,
      gate.position?.z,
    ].join('|')

    // jeśli już mamy cutout i NIC się nie zmieniło -> nie spamuj
    if (gate?.closedBoundingBox && gate?.positionAdjusted && lastCutSigRef.current === sig) return

    // jeśli brama jest otwarta (np. user kliknął) to nie aktualizuj wycięcia,
    // bo i tak wycięcie ma być od pozycji "closed"
    if (isOpenRef.current) return

    // ✅ wymuś, że jesteśmy w pozie zamkniętej zanim policzysz bbox
    freezeClosedPose()

    const box = new THREE.Box3().setFromObject(group.current)
    const size = new THREE.Vector3()
    box.getSize(size)

    const updateData = {
      boundingData: { box, size },
    }

    // Twoja logika wycinania (zostaje)
    const x = (-props.length / 100) / 2 + 0.5
    const z = gate.position.z
    const adjustedY = gate.height / 100 / 2

    updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
    updateData.positionAdjusted = [x - 0.5, adjustedY, z]

    lastCutSigRef.current = sig
    onUpdate(updateData)
  }, [
    onUpdate,
    props.length,
    gate.width,
    gate.height,
    gate.position?.x,
    gate.position?.y,
    gate.position?.z,
    gate?.closedBoundingBox,
    gate?.positionAdjusted,
    freezeClosedPose,
  ])

  useGateBoundingDataChecker({
    nodes,
    gate,
    onBoundingDataChanged: handleBoundingDataChanged,
  })

  // ===== Scaled geometries =====
  const scaledDoorFrameGeometry = useMemo(
    () => scaleGateGeometry(nodes.Door_Frame.geometry, gate.height, gate.width),
    [nodes.Door_Frame.geometry, gate.height, gate.width]
  )

  const scaledDoorLeftGeometry = useMemo(
    () => scaleGateGeometry(nodes.Door_Left.geometry, gate.height, gate.width),
    [nodes.Door_Left.geometry, gate.height, gate.width]
  )

  const scaledMesh003Geometry = useMemo(
    () => scaleGateGeometry(nodes.Mesh003.geometry, gate.height, gate.width),
    [nodes.Mesh003.geometry, gate.height, gate.width]
  )

  const scaledMesh0031Geometry = useMemo(
    () => scaleGateGeometry(nodes.Mesh003_1.geometry, gate.height, gate.width),
    [nodes.Mesh003_1.geometry, gate.height, gate.width]
  )

  // ===== Click handler =====
  const handleClick = useCallback(() => {
    if (!mixerRef.current || !scaledAnimations || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)

    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current?.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpenRef.current) {
      action.time = 0
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    isOpenRef.current = !isOpenRef.current
    setIsOpen((prev) => !prev)
  }, [scaledAnimations])

  // ===== Gate material (NIE RUSZAM) =====
  const gateMaterial = useMemo(() => {
    const base = materials?.Material ?? materials?.['Dark metal.001']
    const gateColor = textures?.gates_double_leaf_color
    if (!base || !gateColor) return undefined

    const newMaterial = base.clone()
    newMaterial.side = THREE.DoubleSide

    const repeatX = gate.width / 100 / 2 / 2 / gateColor.multiplierHorizontal
    const repeatY = 1 / gateColor.multiplierVertical

    newMaterial.map = gateColor.textureDiff ?? null
    newMaterial.metalnessMap = gateColor.textureMetalness ?? null
    newMaterial.normalMap = gateColor.textureNormalness ?? null

    if (newMaterial.map) setupTextureRepeat(newMaterial.map, repeatX, repeatY)
    if (newMaterial.metalnessMap) setupTextureRepeat(newMaterial.metalnessMap, repeatX, repeatY)
    if (newMaterial.normalMap) setupTextureRepeat(newMaterial.normalMap, repeatX, repeatY)

    newMaterial.metalness = 1
    newMaterial.roughness = 0.5
    newMaterial.color = new THREE.Color(gateColor.hex)
    newMaterial.needsUpdate = true

    return newMaterial
  }, [materials, gate.width, textures])

  useEffect(() => {
    return () => {
      gateMaterial?.dispose?.()
    }
  }, [gateMaterial])

  // ===== Precomputed positions (zostaje) =====
  const gateHeightScale = gate.height / 100
  const gateWidthScale = gate.width / 100

  const fallbackMaterial = materials?.['Dark metal.001'] ?? materials?.Material

  return (
    <group
      ref={group}
      wall={'front_wall'}
      dispose={null}
      position={[gate.position.x, gate.position.y, gate.position.z]}
      castShadow={true}
    >
      <group name="Scene" onClick={handleClick}>
        <mesh
          name="Door_Frame"
          geometry={scaledDoorFrameGeometry}
          material={materials?.['Dark metal.001'] ?? fallbackMaterial}
        />
        <mesh
          name="Door_Left"
          geometry={scaledDoorLeftGeometry}
          material={gateMaterial ?? fallbackMaterial}
          position={[-0.498, gateHeightScale * 0.511, gateWidthScale * -0.484]}
        />
        <group
          name="Door_Right"
          position={[-0.498, gateHeightScale * 0.511, gateWidthScale * 0.486]}
        >
          <mesh
            name="Mesh003"
            geometry={scaledMesh003Geometry}
            material={gateMaterial ?? fallbackMaterial}
          />
          <mesh
            name="Mesh003_1"
            geometry={scaledMesh0031Geometry}
            material={materials?.['Dark metal.001'] ?? fallbackMaterial}
            position={[0, 0, gateWidthScale * -0.838]}
          />
        </group>
      </group>
    </group>
  )
})

export default DrzwiFramuga

useGLTF.preload('/models/gates/Brama-dwuskrzydlowa-2.glb')
